import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const EST_ZONE = "America/New_York";
dayjs.tz.setDefault(EST_ZONE);

const getNowEst = () => dayjs().tz(EST_ZONE);
const toEstDay = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) {
    return value.tz ? value.tz(EST_ZONE) : dayjs(value).tz(EST_ZONE);
  }
  return dayjs.tz(value, EST_ZONE);
};

const fmt = (d) => (d ? toEstDay(d).startOf("day").format("YYYY-MM-DD") : "");

const SLOT_CONFIG = {
  morning: {
    label: "MORNING",
    range: "(8AM - 12PM)",
    startHour: 8,
    endHour: 12,
  },
  afternoon: {
    label: "AFTERNOON",
    range: "(12PM - 3PM)",
    startHour: 12,
    endHour: 15,
  },
  evening: {
    label: "EVENING",
    range: "(3PM - 5PM)",
    startHour: 15,
    endHour: 17,
  },
  allDay: {
    label: "ALL DAY",
    range: "(8AM - 5PM)",
    startHour: 8,
    endHour: 17,
  },
};

const SLOT_ORDER = ["morning", "afternoon", "evening", "allDay"];

const Step6 = () => {
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext();
  const [now, setNow] = useState(getNowEst());

  useEffect(() => {
    const timer = setInterval(() => setNow(getNowEst()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (values.available_from) {
      if (!values.available_to) {
        const start = toEstDay(values.available_from)?.startOf("day");
        if (start) {
          setFieldValue("available_to", fmt(start.add(6, "day")));
        }
      }
      return;
    }
    const today = getNowEst().startOf("day");
    setFieldValue("available_from", fmt(today));
    setFieldValue("available_to", fmt(today.add(6, "day")));
  }, [values.available_from, values.available_to, setFieldValue]);

  const startDate =
    values.available_from && dayjs(values.available_from).isValid()
      ? toEstDay(values.available_from).startOf("day")
      : getNowEst().startOf("day");
  const startIso = startDate.format("YYYY-MM-DD");

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, idx) =>
        dayjs.tz(startIso, EST_ZONE).add(idx, "day")
      ),
    [startIso]
  );

  const filterAvailabilityToWeek = (availability = [], rangeStartIso) => {
    const allowed = new Set(
      Array.from({ length: 7 }, (_, idx) =>
        dayjs(rangeStartIso).add(idx, "day").format("YYYY-MM-DD")
      )
    );
    return availability.filter((entry) => {
      if (!entry || entry.indexOf("|") === -1) {
        return false;
      }
      const [iso] = entry.split("|");
      return allowed.has(iso);
    });
  };

  useEffect(() => {
    if (!values.days_available?.length) return;
    const filtered = filterAvailabilityToWeek(values.days_available, startIso);
    if (filtered.length !== values.days_available.length) {
      setFieldValue("days_available", filtered);
    }
  }, [startIso, setFieldValue, values.days_available]);

  useEffect(() => {
    if (!values.days_available?.length) return;
    const filtered = values.days_available.filter((entry) => {
      if (!entry || entry.indexOf("|") === -1) {
        return false;
      }
      const [iso, slotKey] = entry.split("|");
      const slot = SLOT_CONFIG[slotKey];
      if (!slot) {
        return false;
      }
      const slotEnd = dayjs
        .tz(iso, EST_ZONE)
        .hour(slot.endHour)
        .minute(slot.endMinute ?? 0)
        .second(0);
      return slotEnd.isAfter(now);
    });
    if (filtered.length !== values.days_available.length) {
      setFieldValue("days_available", filtered);
    }
  }, [now, setFieldValue, values.days_available]);

  const buildAvailabilityKey = (date, timeSlot) => `${fmt(date)}|${timeSlot}`;

  const isTimeSlotSelected = (dayDate, timeSlot) =>
    (values.days_available || []).includes(buildAvailabilityKey(dayDate, timeSlot));

  const isSlotDisabled = (dayDate, timeSlot) => {
    const slot = SLOT_CONFIG[timeSlot];
    if (!slot) return false;
    const slotEnd = toEstDay(dayDate)
      .hour(slot.endHour)
      .minute(slot.endMinute ?? 0)
      .second(0);
    return slotEnd.isBefore(now);
  };

  const handleAvailabilityChange = (dayDate, timeSlot) => {
    if (isSlotDisabled(dayDate, timeSlot)) {
      return;
    }

    const key = buildAvailabilityKey(dayDate, timeSlot);
    const currentAvailability = values.days_available || [];
    const isSelected = currentAvailability.includes(key);
    let newAvailability = currentAvailability.filter(
      (entry) => entry && entry.indexOf("|") !== -1
    );
    const datePrefix = `${fmt(dayDate)}|`;

    if (timeSlot === "allDay") {
      newAvailability = newAvailability.filter((entry) => !entry.startsWith(datePrefix));
      if (!isSelected) {
        newAvailability.push(key);
      }
    } else {
      newAvailability = newAvailability.filter((entry) => entry !== `${datePrefix}allDay`);
      if (isSelected) {
        newAvailability = newAvailability.filter((entry) => entry !== key);
      } else {
        newAvailability.push(key);
      }
    }

    setFieldValue("days_available", newAvailability);
    setFieldTouched("days_available", true, false);
  };

  const handlePairedDateChange = (name, date) => {
    if (!date) {
      setFieldValue("available_from", "");
      setFieldValue("available_to", "");
      setFieldValue("days_available", []);
      return;
    }

    const normalized = toEstDay(date)?.startOf("day");
    if (!normalized) return;

    if (name === "available_from") {
      setFieldValue("available_from", fmt(normalized));
      setFieldValue("available_to", fmt(normalized.add(6, "day")));
    } else if (name === "available_to") {
      setFieldValue("available_to", fmt(normalized));
      setFieldValue("available_from", fmt(normalized.subtract(6, "day")));
    }
  };

  const fromDay = values.available_from ? toEstDay(values.available_from) : null;
  const toDay = values.available_to ? toEstDay(values.available_to) : null;

  return (
    <div className="w-full">
      {/* Date Range Selector */}
      <div className="flex flex-wrap gap-2 mb-3">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] font-semibold text-[#333333] mb-0.5">
              Available From:
            </label>
            <DatePicker
              value={fromDay}
              onChange={(date) => handlePairedDateChange("available_from", date)}
              disablePast
              // Earliest selectable day is today in EST
              minDate={now.startOf("day")}
              slotProps={{
                textField: {
                  placeholder: "Select start date",
                  fullWidth: true,
                  error: !!(errors.available_from && touched.available_from),
                  helperText:
                    errors.available_from && touched.available_from
                      ? errors.available_from
                      : "",
                  sx: {
                    border: "none",
                    "& .MuiInputBase-root": {
                      backgroundColor: "#F8F8F8",
                      borderRadius: "6px",
                      border: "none",
                      height: "32px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  },
                },
              }}
            />
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] font-semibold text-[#333333] mb-0.5">
              Available To:
            </label>
            <DatePicker
              value={toDay}
              onChange={(date) => handlePairedDateChange("available_to", date)}
              disablePast
              // Ensure the end date stays exactly six days after the start
              minDate={fromDay ? fromDay.add(6, "day") : now.startOf("day").add(6, "day")}
              slotProps={{
                textField: {
                  placeholder: "Select end date",
                  fullWidth: true,
                  error: !!(errors.available_to && touched.available_to),
                  helperText:
                    errors.available_to && touched.available_to
                      ? errors.available_to
                      : "",
                  sx: {
                    border: "none",
                    "& .MuiInputBase-root": {
                      backgroundColor: "#F8F8F8",
                      borderRadius: "6px",
                      border: "none",
                      height: "32px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  },
                },
              }}
            />
          </div>
        </LocalizationProvider>
      </div>

      {/* Availability Table */}
      <div className="overflow-x-auto mb-1 rounded-[8px]">
        <table className="w-full rounded-[8px] border border-[#ECECEC]">
          <thead className="bg-[#F3F3F3] rounded-[8px]">
            <tr>
              <th className="p-1.5 text-left font-medium text-[#242424] w-1/5 text-xs">DAYS</th>
              {SLOT_ORDER.map((slotKey) => (
                <th key={slotKey} className="p-1.5 text-center font-medium text-[#242424] w-1/5 text-xs">
                  <div>{SLOT_CONFIG[slotKey].label}</div>
                  <div className="text-[9px] font-normal text-[#242424]">
                    {SLOT_CONFIG[slotKey].range}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weekDays.map((dayDate) => {
              const dayKey = fmt(dayDate);
              return (
                <tr key={dayKey} className="border-t border-[#ECECEC]">
                  <td className="p-1.5 font-medium text-xs">
                    <div>{dayDate.format("dddd")}</div>
                    <div className="text-[9px] text-[#6B6B6B]">{dayDate.format("MMM D")}</div>
                  </td>
                  {SLOT_ORDER.map((timeSlot) => {
                    const isSelected = isTimeSlotSelected(dayDate, timeSlot);
                    const disabled = isSlotDisabled(dayDate, timeSlot);
                    return (
                      <td key={`${dayKey}-${timeSlot}`} className="p-1.5 text-center">
                        <label
                          className={`inline-flex items-center justify-center ${
                            disabled ? "cursor-not-allowed" : "cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={isSelected}
                            disabled={disabled}
                            onChange={() => handleAvailabilityChange(dayDate, timeSlot)}
                          />
                          <div
                            className={`w-4 h-4 border rounded flex items-center justify-center ${
                              isSelected
                                ? "bg-[#52C171] border-[#52C171]"
                                : disabled
                                ? "bg-[#E5E5E5] border-[#D6D6D6]"
                                : "border-gray-300"
                            } ${disabled ? "opacity-60" : ""}`}
                          >
                            {isSelected && (
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10 3L4.5 8.5L2 6"
                                  stroke="white"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </label>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {errors.days_available && touched.days_available && (
        <p className="text-red-500 text-xs font-medium mb-2">
          {errors.days_available}
        </p>
      )}
    </div>
  );
};

export default Step6;
